import React, { useContext, useEffect } from 'react';
import { LogoLoading } from 'ensightful-koala-ui';
import { cloneDeep, omit } from 'lodash';
import moment from 'moment';
import { styled } from '@mui/material';
import uuid from 'uuid';

import { UploadResourcesModal } from 'components/organisms/Task';
import { TemplateDrawerInner, FilterDrawerInner, ScheduleModal } from 'components/organisms/TaskTemplates';
import { useAuth } from 'contexts/auth';
import { useInstructor } from 'contexts/instructorRole';
import { useSnackbar } from 'contexts/snackbar';
import { useUser2 } from 'contexts/user2';
import { useViewport } from 'contexts/viewport';
import withResponsiveLayout from 'hoc/withResponsive';
import Mobile from 'pages/TaskManager/TaskTemplates/mobile';
import Tablet from 'pages/TaskManager/TaskTemplates/tablet';
import Desktop from 'pages/TaskManager/TaskTemplates/desktop';
import { PageContext } from 'pages/TaskManager/PageProvider';
import courseService from 'services/course';
import taskService from 'services/tasks';
import { AlertDialog, Drawer } from 'shell/components';
import { TYPES } from 'utils/constants';
import {
    getTemplateCompletion,
    getTemplateCompletionFraction,
    getTemplateStatus,
    getTemplateStatusWithIcon,
} from 'utils/helper';

const countFilters = (filterState) => {
    let count = 0;
    if (filterState.sortKey !== 'none') count++;
    if (filterState.statusFilterKeys.length) count++;
    if (filterState.dueDateFilterKeys.length) count++;
    return count;
};

const TaskTemplates = () => {
    const { auth } = useAuth();
    const { courseLoading, taskTemplates, milestones } = useInstructor();
    const { message } = useSnackbar();
    const { groups } = useUser2();
    const { width } = useViewport();
    const {
        calendarOpen,
        setCalendarOpen,
        spawnedTasks,
        templateController,
        checkedTemplates: checked,
        setCheckedTemplates: setChecked,
        didFetchCompletion,
        setDidFetchCompletion,
        files,
        setFiles,
        sharedUrls,
        setSharedUrls,
        changes,
        setChanges,
        drawerState,
        setDrawerState,
        filterState,
        setFilterState,
        uploadResources,
        setUploadResources,
        confirmationModal,
        setConfirmationModal,
        scheduleModal,
        setScheduleModal,
        updateTaskTemplate,
    } = useContext(PageContext);

    const closeConfirmationModal = () => {
        setConfirmationModal((prev) => ({ ...prev, visible: false, onConfirm: () => {}, onCancel: () => {} }));
    };

    const getSortedFilteredTemplates = (taskTemplates) => {
        return taskTemplates
            .filter((template) => !template.isLocal)
            .filter((template) => {
                return template.title.toLowerCase().includes(filterState.searchInput.toLowerCase());
            })
            .filter((template) => {
                if (!filterState.statusFilterKeys.length) return true;
                return filterState.statusFilterKeys.some((key) => {
                    return getTemplateStatus(template).toLowerCase().includes(key);
                });
            })
            .filter((template) => {
                if (!filterState.dueDateFilterKeys.length) return true;
                const today = moment(new Date()).hour(0).minute(0).second(0).millisecond(0);
                const dueDate = moment(template.end_on);
                if (filterState.dueDateFilterKeys.includes('today')) {
                    return dueDate.format('D M YYYY') === today.format('D M YYYY');
                }
                if (filterState.dueDateFilterKeys.includes('thisweek')) {
                    const startOfWeek = today.clone().day(0).add(1, 'day');
                    const endOfWeek = today.clone().day(6).add(1, 'day');
                    return dueDate.isSameOrAfter(startOfWeek) && dueDate.isSameOrBefore(endOfWeek);
                }
                if (filterState.dueDateFilterKeys.includes('nextweek')) {
                    const startOfNextWeek = today.clone().add(7, 'days').day(0).add(1, 'day');
                    const endOfNextWeek = today.clone().add(7, 'days').day(6).add(1, 'day');
                    return dueDate.isSameOrAfter(startOfNextWeek) && dueDate.isSameOrBefore(endOfNextWeek);
                }
                if (filterState.dueDateFilterKeys.includes('overdue')) {
                    return dueDate.toDate() < today.toDate();
                }
                return false;
            })
            .sort((a, b) => {
                if (filterState.sortKey === 'none') {
                    return b.created_on - a.created_on;
                }
                if (filterState.sortKey === 'titlea-z') {
                    return a.title.toLowerCase() > b.title.toLowerCase() ? 1 : -1;
                }
                if (filterState.sortKey === 'titlez-a') {
                    return a.title.toLowerCase() < b.title.toLowerCase() ? 1 : -1;
                }
                if (filterState.sortKey === 'closestdue') {
                    return a.end_on - b.end_on;
                }
                if (filterState.sortKey === 'furthestdue') {
                    return b.end_on - a.end_on;
                }
                if (filterState.sortKey === 'completed') {
                    if (getTemplateStatus(a) !== 'Published' && getTemplateStatus(b) !== 'Published') return 0;
                    if (getTemplateStatus(a) !== 'Published') return 1;
                    if (getTemplateStatus(b) !== 'Published') return -1;
                    return getTemplateCompletionFraction(spawnedTasks)(a) <
                        getTemplateCompletionFraction(spawnedTasks)(b)
                        ? 1
                        : -1;
                }
                if (filterState.sortKey === 'incomplete') {
                    if (getTemplateStatus(a) !== 'Published' && getTemplateStatus(b) !== 'Published') return 0;
                    if (getTemplateStatus(a) !== 'Published') return 1;
                    if (getTemplateStatus(b) !== 'Published') return -1;
                    return getTemplateCompletionFraction(spawnedTasks)(a) >
                        getTemplateCompletionFraction(spawnedTasks)(b)
                        ? 1
                        : -1;
                }
                return 0;
            });
    };

    const onChangeCheck = (templateId) => {
        setChecked((current) => {
            return current.includes(templateId) ? current.filter((id) => id !== templateId) : [...current, templateId];
        });
    };

    const onChangeCheckAll = (checked) => {
        if (checked) {
            setChecked(taskTemplates.map(({ id }) => id));
        } else {
            setChecked([]);
        }
    };

    const getAssocMilestone = (template) => {
        return milestones.find(({ id }) => id === template.milestone_id);
    };

    const onClickTemplate = (templateId) => {
        if (Object.keys(changes).length) return;
        setDrawerState({ isOpen: true, templateId });
    };

    const closeDrawer = () => {
        const template = taskTemplates.find((template) => template.id === drawerState.templateId);
        if (template?.isLocal && !confirmationModal.visible) {
            setConfirmationModal({
                visible: true,
                destructive: true,
                title: 'Leave Task Creation?',
                content: 'All changes made to this task will not be saved.',
                confirmText: 'Leave',
                onConfirm: () => {
                    closeConfirmationModal();
                    setDrawerState((current) => ({ ...current, isOpen: false }));
                    deleteTemplate(template.id);
                },
                onCancel: closeConfirmationModal,
            });
            return;
        }
        if (!template?.isLocal) {
            if (!Boolean(Object.keys(omit(changes, ['published_on'])).length)) {
                setDrawerState((current) => ({ ...current, isOpen: false }));
                setChanges({});
            } else {
                setConfirmationModal({
                    visible: true,
                    destructive: true,
                    title: 'Discard Changes?',
                    content: 'All changes made to this task will not be saved.',
                    confirmText: 'Leave',
                    onConfirm: () => {
                        closeConfirmationModal();
                        setDrawerState((current) => ({ ...current, isOpen: false }));
                        setChanges({});
                    },
                    onCancel: closeConfirmationModal,
                });
            }
        }
    };

    const closeFilterDrawer = () => {
        setFilterState((prev) => ({ ...prev, isOpen: false }));
    };

    const submitResources = (files, link) => {
        const newResources = [];
        if (files.length) {
            const resource = {
                id: uuid.v4(),
                active: true,
                owner_account_id: auth.owner_account_id,
                created_on: new Date().getTime(),
                modified_on: new Date().getTime(),
                course_id: auth.course_id,
                type: 'file',
                data: files[0],
            };
            newResources.push(resource);
            setFiles((current) => ({
                ...current,
                [resource.id]: { ...resource, type: TYPES.TEMPLATE, template_id: drawerState.templateId },
            }));
        }
        if (link.name && link.url) {
            const resource = {
                id: uuid.v4(),
                active: true,
                owner_account_id: auth.owner_account_id,
                created_on: new Date().getTime(),
                modified_on: new Date().getTime(),
                course_id: auth.course_id,
                type: 'link',
                url: link.url,
                name: link.name,
            };
            newResources.push(resource);
            setSharedUrls((current) => ({
                ...current,
                [resource.id]: { ...resource, type: TYPES.TEMPLATE, template_id: drawerState.templateId },
            }));
        }
        const template = taskTemplates.find((template) => template.id === drawerState.templateId);
        if (template.isLocal) {
            const newTemplate = cloneDeep(template);
            newTemplate.resources = (newTemplate.resources || []).concat(newResources);
            updateTemplate(template.id, newTemplate);
        } else {
            setChanges((current) => {
                return {
                    ...current,
                    resources: [
                        ...(current.resources || []),
                        ...newResources.map((resource) => ({ ...resource, action: 'add' })),
                    ],
                };
            });
        }
        setUploadResources((current) => ({ ...current, isOpen: false }));
    };

    const onClickCreate = () => {
        const newTemplate = templateController.create();
        setDrawerState({ isOpen: true, templateId: newTemplate.id });
    };

    const onClickPublishMulti = () => {
        setConfirmationModal({
            visible: true,
            destructive: false,
            title: 'Publish Selected Task Templates?',
            content: 'All selected task templates will be published.',
            confirmText: 'Publish',
            onConfirm: () => {
                closeConfirmationModal();
                taskTemplates
                    .filter(({ id }) => checked.includes(id))
                    .filter(({ published_on }) => published_on === 0 || published_on > new Date().getTime())
                    .forEach(({ id }) => publish(id, {}, false));
                message.success('All selected task templates successfully published.');
                setChecked([]);
            },
            onCancel: closeConfirmationModal,
        });
    };

    const onClickDeleteMulti = () => {
        setConfirmationModal({
            visible: true,
            destructive: true,
            title: 'Delete Selected Task Templates?',
            content: 'All selected task templates will be unpublished and deleted.',
            confirmText: 'Delete',
            onConfirm: () => {
                closeConfirmationModal();
                taskTemplates.filter(({ id }) => checked.includes(id)).forEach(({ id }) => deleteTemplate(id, false));
                message.success('All selected task templates successfully unpublished and deleted.');
                setChecked([]);
            },
            onCancel: closeConfirmationModal,
        });
    };

    const duplicateTemplate = (originalTemplateId) => {
        templateController.duplicate(originalTemplateId);
        message.success('Your task template was successfully duplicated.');
    };

    const deleteTemplateWithConfirmation = (templateId) => {
        setConfirmationModal({
            visible: true,
            destructive: true,
            title: 'Delete Task Template?',
            content: 'This task template will be unpublished and deleted.',
            confirmText: 'Delete',
            onConfirm: () => {
                closeConfirmationModal();
                deleteTemplate(templateId);
            },
            onCancel: closeConfirmationModal,
        });
    };

    const updateTemplate = (templateId, changes) => {
        templateController.update(templateId, changes);
    };

    const deleteTemplate = (templateId, showSuccessMessage) => {
        templateController.delete(templateId, showSuccessMessage);
        setChanges({});
        setDrawerState((current) => ({ ...current, isOpen: false }));
    };

    const save = (templateId, changes, shouldNotify) => {
        const isLocal = templateController.save(templateId, changes, shouldNotify);
        if (!isLocal) setChanges({});
        setDrawerState((current) => ({ ...current, isOpen: false }));
    };

    const publish = async (templateId, changes, showSuccessMessage) => {
        const isLocal = await templateController.publish(templateId, changes, showSuccessMessage);
        if (!isLocal) setChanges({});
        setDrawerState((current) => ({ ...current, isOpen: false }));
    };

    const unpublish = (templateId) => {
        templateController.unpublish(templateId);
        setChanges({});
        setDrawerState((current) => ({ ...current, isOpen: false }));
    };

    const schedule = async (templateId, changes, timestamp) => {
        const isLocal = await templateController.schedule(templateId, changes, timestamp);
        if (!isLocal) setChanges({});
        setDrawerState((current) => ({ ...current, isOpen: false }));
    };

    const unschedule = (templateId) => {
        templateController.unschedule(templateId);
        setChanges({});
        setDrawerState((current) => ({ ...current, isOpen: false }));
    };

    const setScheduleTime = (timestamp) => {
        const template = taskTemplates.find((template) => template.id === drawerState.templateId);
        if (template.isLocal) {
            schedule(template.id, changes, timestamp);
        } else {
            schedule(template.id, { ...changes, published_on: timestamp });
        }
        setScheduleModal({ visible: false });
    };

    const handleMultiDuedate = (date) => {
        setConfirmationModal({
            visible: true,
            destructive: false,
            title: 'Update Due Date(s)?',
            content: `This will change the due date(s) of (${checked.length}) task templates. Do you want to continue?`,
            confirmText: 'Continue',
            onConfirm: () => {
                closeConfirmationModal();
                setCalendarOpen(false);
                taskTemplates
                    .filter(({ id }) => checked.includes(id))
                    .forEach(({ id }) => {
                        updateTemplate(id, { end_on: date.valueOf() }, false);
                        updateTaskTemplate(id, { end_on: date.valueOf() }, false);
                    });
                message.success('All selected milestones successfully updated.');
                setChecked([]);
            },
            onCancel: closeConfirmationModal,
        });
    };

    const props = {
        filterState,
        numFilters: countFilters(filterState),
        setFilterState,
        calendarOpen,
        setCalendarOpen,
        taskTemplates: getSortedFilteredTemplates(taskTemplates),
        checked,
        onChangeCheck,
        onChangeCheckAll,
        getAssocMilestone,
        getTemplateStatus,
        getTemplateStatusWithIcon,
        getCompletion: getTemplateCompletion(spawnedTasks),
        onClickTemplate,
        onClickCreate,
        onClickPublishMulti,
        onClickDeleteMulti,
        duplicateTemplate,
        deleteTemplateWithConfirmation,
        handleMultiDuedate,
    };

    const templateDrawerProps = {
        open: drawerState.isOpen,
        onClose: closeDrawer,
    };

    const templateDrawerInnerProps = {
        isOpen: drawerState.isOpen,
        template: taskTemplates.find((template) => template.id === drawerState.templateId),
        files,
        sharedUrls,
        changes,
        groups,
        spawnedTasks,
        setFiles,
        setSharedUrls,
        updateTemplate,
        setChanges,
        setDrawerState,
        setUploadResources,
        setConfirmationModal,
        closeConfirmationModal,
        setScheduleModal,
        save,
        publish,
        unpublish,
        unschedule,
        deleteTemplate,
    };

    const filterDrawerProps = {
        anchor: 'left',
        open: filterState.isOpen,
        onClose: closeFilterDrawer,
    };

    const filterDrawerInnerProps = {
        filterState,
        setFilterState,
    };

    const uploadResourcesProps = {
        isOpen: uploadResources.isOpen,
        onClose: () => setUploadResources((current) => ({ ...current, isOpen: false })),
        submitResources,
    };

    const responsiveLayouts = {
        mobile: <Mobile {...props} />,
        tablet: <Tablet {...props} />,
        desktop: <Desktop {...props} />,
    };

    const responsiveTemplateDrawers = {
        mobile: (
            <CustomDrawer device={'mobile'} {...templateDrawerProps}>
                <TemplateDrawerInner {...templateDrawerInnerProps} />
            </CustomDrawer>
        ),
        tablet: (
            <CustomDrawer device={'tablet'} {...templateDrawerProps}>
                <TemplateDrawerInner {...templateDrawerInnerProps} />
            </CustomDrawer>
        ),
        desktop: (
            <CustomDrawer device={'desktop'} {...templateDrawerProps}>
                <TemplateDrawerInner {...templateDrawerInnerProps} />
            </CustomDrawer>
        ),
    };

    const responsiveFilterDrawers = {
        mobile: (
            <Drawer device={'mobile'} {...filterDrawerProps}>
                <FilterDrawerInner {...filterDrawerInnerProps} />
            </Drawer>
        ),
        tablet: (
            <Drawer device={'tablet'} {...filterDrawerProps}>
                <FilterDrawerInner {...filterDrawerInnerProps} />
            </Drawer>
        ),
        desktop: (
            <Drawer device={'desktop'} {...filterDrawerProps}>
                <FilterDrawerInner {...filterDrawerInnerProps} />
            </Drawer>
        ),
    };

    const responsiveUploadResources = {
        mobile: <UploadResourcesModal device={'mobile'} {...uploadResourcesProps} />,
        tablet: <UploadResourcesModal device={'tablet'} {...uploadResourcesProps} />,
        desktop: <UploadResourcesModal device={'desktop'} {...uploadResourcesProps} />,
    };

    useEffect(() => {
        if (courseLoading) return;
        if (!didFetchCompletion) {
            (async () => {
                await Promise.all(
                    taskTemplates.map(async (template) => {
                        return await Promise.all(
                            template.task_ids.map(async (taskId) => {
                                const task = await taskService.findTask(taskId);
                                spawnedTasks[taskId] = task;
                            })
                        );
                    })
                );
                setDidFetchCompletion(true);
            })();
        }
    }, [courseLoading, didFetchCompletion]);

    useEffect(() => {
        if (drawerState.isOpen && drawerState.templateId) {
            (async () => {
                const template = taskTemplates.find((template) => template.id === drawerState.templateId);
                const files = await courseService.listCourseFiles(
                    template.course_id,
                    JSON.stringify({
                        type: TYPES.TEMPLATE,
                        template_id: template.id,
                    })
                );
                const sharedUrls = await courseService.listCourseSharedUrls(
                    template.course_id,
                    JSON.stringify({
                        type: TYPES.TEMPLATE,
                        template_id: template.id,
                    })
                );
                setFiles((prev) => files.reduce((acc, cur) => ({ ...acc, [cur.id]: cur }), prev));
                setSharedUrls((prev) => sharedUrls.reduce((acc, cur) => ({ ...acc, [cur.id]: cur }), prev));
                await Promise.all(
                    template.task_ids.map(async (taskId) => {
                        const task = await taskService.findTask(taskId);
                        spawnedTasks[taskId] = task;
                    })
                );
            })();
        }
    }, [drawerState.isOpen, drawerState.templateId]);

    return courseLoading ? (
        <LogoLoading />
    ) : (
        <>
            {withResponsiveLayout(width, responsiveLayouts)}
            {withResponsiveLayout(width, responsiveTemplateDrawers)}
            {withResponsiveLayout(width, responsiveFilterDrawers)}
            {withResponsiveLayout(width, responsiveUploadResources)}
            <AlertDialog
                message={confirmationModal.title}
                description={confirmationModal.content}
                open={confirmationModal.visible}
                variant={confirmationModal.destructive ? 'destructive' : 'primary'}
                actionButtonText={confirmationModal.confirmText}
                onAction={confirmationModal.onConfirm}
                onCancel={confirmationModal.onCancel}
                onClose={closeConfirmationModal}
            />
            <ScheduleModal
                visible={scheduleModal.visible}
                onSchedule={setScheduleTime}
                onCancel={() => setScheduleModal({ visible: false })}
            />
        </>
    );
};

const CustomDrawer = styled(Drawer)(() => {
    return {
        '& .MuiDrawer-paper > .MuiBox-root': {
            height: 'calc(100% - 64px - 69px)',
        },
    };
});

export default TaskTemplates;
